/**
 * ConceptTagPicker — multi-select typeahead for tagging a question or solution
 * with concept ids (Phase 2). Selected concepts show as removable chips; typing
 * filters the available concepts by name + aliases (the alias auto-suggest from
 * the design). Purely presentational: the available list is supplied by the host
 * via QuestionEditForm's `onLoadConcepts`, and selection flows out through onChange.
 *
 * See docs/FeatureDesigns/modules-curriculum-topics-concepts/concept-curriculum-model-implplan.md (Phase 2).
 */
import { useMemo, useState } from 'react';
import type { ConceptOption } from '../formTypes';

export interface ConceptTagPickerProps {
    value: string[];
    onChange: (ids: string[]) => void;
    concepts: ConceptOption[];
    placeholder?: string;
}

export function ConceptTagPicker({ value, onChange, concepts, placeholder }: ConceptTagPickerProps) {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);

    const byId = useMemo(() => new Map(concepts.map(c => [c.id, c])), [concepts]);
    const selected = value ?? [];

    const matches = useMemo(() => {
        const q = query.trim().toLowerCase();
        return concepts
            .filter(c => !selected.includes(c.id))
            .filter(c => !q
                || c.name.toLowerCase().includes(q)
                || c.aliases?.some(a => a.toLowerCase().includes(q)))
            .slice(0, 8);
    }, [concepts, selected, query]);

    const add = (id: string) => {
        if (!selected.includes(id)) onChange([...selected, id]);
        setQuery('');
        setOpen(false);
    };
    const remove = (id: string) => onChange(selected.filter(x => x !== id));

    return (
        <div className="concept-tag-picker" style={{ position: 'relative' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                {selected.map(id => {
                    const c = byId.get(id);
                    return (
                        <span key={id} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '2px 8px', borderRadius: 12, fontSize: 12,
                            // Theme-adaptive soft chip: both tokens flip per theme, so contrast holds
                            // in light (dark-indigo text on pale bg) and dark (lavender text on deep-indigo bg).
                            background: 'var(--color-primary-light, #eef2ff)', color: 'var(--color-primary, #4f46e5)',
                        }}>
                            {c ? c.name : <em title="Unknown concept id">{id}</em>}
                            <button type="button" onClick={() => remove(id)}
                                style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, lineHeight: 1, color: 'inherit' }}>
                                ×
                            </button>
                        </span>
                    );
                })}
                {selected.length === 0 && (
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>No concepts tagged</span>
                )}
            </div>
            <input
                type="text"
                className="form-input"
                value={query}
                placeholder={placeholder ?? 'Type to find a concept…'}
                onChange={e => { setQuery(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
            />
            {open && matches.length > 0 && (
                <ul style={{
                    position: 'absolute', zIndex: 10, left: 0, right: 0, margin: '2px 0 0',
                    padding: 4, listStyle: 'none', maxHeight: 220, overflowY: 'auto',
                    background: 'var(--surface, #fff)', border: '1px solid var(--border-color)', borderRadius: 6,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                }}>
                    {matches.map(c => (
                        <li key={c.id}>
                            <button type="button" onMouseDown={() => add(c.id)}
                                style={{
                                    display: 'block', width: '100%', textAlign: 'left', border: 'none',
                                    background: 'none', cursor: 'pointer', padding: '6px 8px', borderRadius: 4,
                                    color: 'var(--text-primary)', fontSize: 13,
                                }}>
                                {c.name}
                                {c.aliases && c.aliases.length > 0 && (
                                    <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}> · {c.aliases.join(', ')}</span>
                                )}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            {concepts.length === 0 && (
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                    No concepts defined for this subject yet — add them on the Concepts page.
                </div>
            )}
        </div>
    );
}
